import { useEffect, useState } from "react";
import { 
  Copy, Users, UserPlus, Shield, User, Key, Mail, Trash2, ShieldAlert 
} from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const TIMEZONES = [
  "Europe/Paris", "Europe/Brussels", "Europe/Geneva", 
  "Europe/Luxembourg", "America/Montreal", "Africa/Casablanca", 
  "Africa/Tunis", "Africa/Abidjan"
];
const BRAND_COLORS = ["#2563EB", "#059669", "#7C3AED", "#DB2777", "#EA580C", "#0D9488"];

export default function Settings() {
  const { user, tenant, reloadTenant } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "superadmin" || !user?.role;

  const [company, setCompany] = useState({ name: "", description: "", timezone: "Europe/Paris" });
  const [page, setPage] = useState(null);
  const [rules, setRules] = useState(null);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState("");

  // Gestion des membres / équipe
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberForm, setMemberForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role: "member",
  });
  const [submittingMember, setSubmittingMember] = useState(false);

  const loadMembers = () => {
    if (!isOwner) return;
    setLoadingMembers(true);
    API.get("/tenant/members")
      .then((res) => setMembers(res.data || []))
      .catch((err) => console.error("Erreur chargement membres:", err))
      .finally(() => setLoadingMembers(false));
  };

  useEffect(() => {
    if (tenant) {
      setCompany({ 
        name: tenant.name || "", 
        description: tenant.description || "", 
        timezone: tenant.timezone || "Europe/Paris" 
      });
      API.get("/tenant/booking-page").then((r) => setPage(r.data)).catch(() => {});
      API.get("/tenant/booking-settings").then((r) => {
        const data = r.data || {};
        setRules({
          ...data,
          min_notice_hours: data.min_notice_minutes != null ? Number((data.min_notice_minutes / 60).toFixed(2)) : 2,
          cancel_notice_hours: data.cancel_notice_minutes != null ? Number((data.cancel_notice_minutes / 60).toFixed(2)) : 2,
        });
      }).catch(() => {});

      loadMembers();
    }
  }, [tenant, isOwner]);

  const bookingUrl = `${window.location.origin}/b/${tenant?.slug || ""}`;

  const save = async (section, fn) => {
    setSaving(section);
    try {
      await fn();
      toast.success("Paramètres enregistrés");
      await reloadTenant();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving("");
    }
  };

  const saveRules = async () => {
    const payload = {
      ...rules,
      min_notice_minutes: Math.max(0, Math.round(Number(rules.min_notice_hours || 0) * 60)),
      cancel_notice_minutes: Math.max(0, Math.round(Number(rules.cancel_notice_hours || 0) * 60)),
      max_advance_days: Number(rules.max_advance_days),
      slot_granularity_min: Number(rules.slot_granularity_min),
    };
    await API.put("/tenant/booking-settings", payload);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      toast.success("Lien copié");
    } catch {
      toast.info(bookingUrl);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!memberForm.email || !memberForm.password) {
      toast.error("Veuillez renseigner un email et un mot de passe.");
      return;
    }

    setSubmittingMember(true);
    try {
      await API.post("/tenant/members", memberForm);
      toast.success("Accès collaborateur créé !");
      setShowAddMemberModal(false);
      setMemberForm({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        role: "member",
      });
      loadMembers();
    } catch (err) {
      toast.error(formatApiError(err, "Échec de création du compte"));
    } finally {
      setSubmittingMember(false);
    }
  };

  const handleDeleteMember = async (membershipId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir révoquer cet accès ?")) return;
    try {
      await API.delete(`/tenant/members/${membershipId}`);
      toast.success("Accès révoqué");
      loadMembers();
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de révoquer l'accès"));
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <h1 className="font-display text-2xl font-bold text-slate-900">Paramètres</h1>

      {/* Lien public */}
      <section className="rounded-xl border border-blue-200 bg-blue-50/50 p-5" data-testid="settings-public-link">
        <h2 className="font-display text-lg font-semibold text-slate-900">Votre lien de réservation public</h2>
        <p className="mt-1 text-sm text-slate-500">Envoyez ce lien par SMS à vos clients.</p>
        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 truncate rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-blue-700" data-testid="public-link-text">{bookingUrl}</code>
          <Button variant="outline" size="icon" onClick={copyLink} data-testid="btn-copy-public-link"><Copy className="h-4 w-4" /></Button>
        </div>
        <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-medium text-blue-600 hover:underline" data-testid="link-preview-public">
          Prévisualiser la page →
        </a>
      </section>

      {/* Gestion de l'équipe et des accès (Réservé au gérant / owner) */}
      {isOwner && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" /> Équipe & Droits d'accès
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Créez des accès pour vos secrétaires et techniciens d'atelier sans exposer votre Chiffre d'Affaires.
              </p>
            </div>
            <Button 
              size="sm" 
              onClick={() => setShowAddMemberModal(true)} 
              className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
            >
              <UserPlus className="mr-1.5 h-4 w-4" /> Ajouter un compte
            </Button>
          </div>

          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200/80 overflow-hidden">
            {loadingMembers ? (
              <p className="p-4 text-center text-xs text-slate-400">Chargement des membres...</p>
            ) : members.length === 0 ? (
              <div className="p-3.5 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-xs">
                    {user?.email?.[0]?.toUpperCase() || "P"}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-900">{user?.email}</p>
                    <p className="text-[11px] text-slate-400">Compte principal</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                  <Shield className="h-3 w-3 text-amber-600" /> Gérant / Direction
                </span>
              </div>
            ) : (
              members.map((m) => {
                const isMemberOwner = m.role === "owner";
                const isSelf = String(m.user_id) === String(user?.id);
                return (
                  <div key={m.membership_id} className="p-3.5 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        isMemberOwner ? "bg-slate-900 text-white" : "bg-blue-100 text-blue-700"
                      }`}>
                        {m.first_name ? m.first_name[0].toUpperCase() : m.email[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          {m.first_name || m.last_name ? `${m.first_name} ${m.last_name}` : m.email}
                          {isSelf && <span className="ml-1 text-[10px] text-slate-400 font-normal">(Vous)</span>}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate">{m.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {isMemberOwner ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-full">
                          <Shield className="h-3 w-3 text-amber-600" /> Gérant (CA visible)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200/80 px-2 py-0.5 rounded-full">
                          <User className="h-3 w-3 text-blue-600" /> Collaborateur
                        </span>
                      )}

                      {!isSelf && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => handleDeleteMember(m.membership_id)}
                          title="Révoquer cet accès"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      )}

      {/* Entreprise */}
      <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="settings-company">
        <h2 className="font-display text-lg font-semibold text-slate-900">Entreprise</h2>
        <div className="mt-4 space-y-4">
          <div>
            <Label>Nom</Label>
            <Input value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="settings-name" className="mt-1" />
          </div>
          <div>
            <Label>Description (affichée sur la page publique)</Label>
            <Input value={company.description} onChange={(e) => setCompany({ ...company, description: e.target.value })} data-testid="settings-description" className="mt-1" />
          </div>
          <div>
            <Label>Fuseau horaire</Label>
            <Select value={company.timezone} onValueChange={(v) => setCompany({ ...company, timezone: v })}>
              <SelectTrigger data-testid="settings-timezone" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => save("company", () => API.put("/tenant", company))} disabled={saving === "company"} data-testid="settings-company-save">
            {saving === "company" ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </section>

      {/* Page de réservation (branding) */}
      {page && (
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="settings-branding">
          <h2 className="font-display text-lg font-semibold text-slate-900">Page de réservation (branding)</h2>
          <div className="mt-4 space-y-4">
            <div>
              <Label>URL du logo</Label>
              <Input value={page.logo_url || ""} onChange={(e) => setPage({ ...page, logo_url: e.target.value })} placeholder="https://..." data-testid="settings-logo" className="mt-1" />
            </div>
            <div>
              <Label>Couleur principale</Label>
              <div className="mt-2 flex gap-2">
                {BRAND_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setPage({ ...page, brand_color: c })}
                    data-testid={`brand-color-${c}`}
                    className={`h-10 w-10 rounded-lg transition-transform ${page.brand_color === c ? "ring-2 ring-slate-900 ring-offset-2 scale-110" : ""}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Message d'accueil</Label>
              <Input value={page.welcome_message || ""} onChange={(e) => setPage({ ...page, welcome_message: e.target.value })} data-testid="settings-welcome" className="mt-1" />
            </div>
            {[["show_description", "Afficher la description"], ["show_price", "Afficher les prix"], ["show_employee", "Afficher le choix de l'employé"]].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <Label>{label}</Label>
                <Switch checked={!!page[key]} onCheckedChange={(v) => setPage({ ...page, [key]: v })} data-testid={`settings-${key}`} />
              </div>
            ))}
            <Button onClick={() => save("branding", () => API.put("/tenant/booking-page", page))} disabled={saving === "branding"} data-testid="settings-branding-save">
              {saving === "branding" ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </section>
      )}

      {/* Règles de réservation */}
      {rules && (
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="settings-rules">
          <h2 className="font-display text-lg font-semibold text-slate-900">Règles de réservation</h2>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Délai minimum avant RDV (heures)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  value={rules.min_notice_hours ?? ""}
                  onChange={(e) => setRules({ ...rules, min_notice_hours: e.target.value })}
                  placeholder="ex: 2 ou 24"
                  data-testid="rules-min-notice"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Réservation max (jours)</Label>
                <Input
                  type="number"
                  min={1}
                  value={rules.max_advance_days}
                  onChange={(e) => setRules({ ...rules, max_advance_days: Number(e.target.value) })}
                  data-testid="rules-max-advance"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Espacement entre chaque rendez-vous</Label>
                <Select value={String(rules.slot_granularity_min)} onValueChange={(v) => setRules({ ...rules, slot_granularity_min: Number(v) })}>
                  <SelectTrigger data-testid="rules-granularity" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 30, 60].map((g) => (
                      <SelectItem key={g} value={String(g)}>{g} minutes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Annulation autorisée jusqu'à (heures)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  value={rules.cancel_notice_hours ?? ""}
                  onChange={(e) => setRules({ ...rules, cancel_notice_hours: e.target.value })}
                  placeholder="ex: 2 ou 48"
                  data-testid="rules-cancel-notice"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <Label>Fermeture automatique les jours fériés</Label>
              </div>
              <Switch checked={!!rules.close_on_holidays} onCheckedChange={(v) => setRules({ ...rules, close_on_holidays: v })} data-testid="rules-close-holidays" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 opacity-60">
              <div>
                <Label>Modification en ligne par le client</Label>
                <p className="text-xs text-slate-400">Disponible dans une prochaine version.</p>
              </div>
              <Switch checked={false} disabled data-testid="rules-allow-reschedule" />
            </div>
            <Button onClick={() => save("rules", saveRules)} disabled={saving === "rules"} data-testid="settings-rules-save">
              {saving === "rules" ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </section>
      )}

      {/* Mot de passe personnel */}
      <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="settings-password">
        <h2 className="font-display text-lg font-semibold text-slate-900">Changer de mot de passe</h2>
        <div className="mt-4 space-y-4">
          <div>
            <Label>Mot de passe actuel</Label>
            <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="pwd-current" className="mt-1" />
          </div>
          <div>
            <Label>Nouveau mot de passe</Label>
            <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="pwd-new" className="mt-1" />
          </div>
          <Button
            variant="outline"
            disabled={!pwd.current_password || pwd.new_password.length < 8 || saving === "pwd"}
            onClick={() => save("pwd", async () => { await API.post("/auth/change-password", pwd); setPwd({ current_password: "", new_password: "" }); })}
            data-testid="pwd-save"
          >
            Changer le mot de passe
          </Button>
        </div>
      </section>

      {/* Fenêtre modale de création d'accès collaborateur */}
      {showAddMemberModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-md w-full shadow-2xl space-y-4 animate-in fade-in zoom-in-95">
            <div>
              <h3 className="font-display text-base font-bold text-slate-900">
                Créer un compte d'accès
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Renseignez les coordonnées de votre secrétaire ou collaborateur.
              </p>
            </div>

            <form onSubmit={handleCreateMember} className="space-y-3.5 pt-1">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Prénom</Label>
                  <Input
                    placeholder="Ex: Sarah"
                    value={memberForm.first_name}
                    onChange={(e) => setMemberForm({ ...memberForm, first_name: e.target.value })}
                    className="h-9 text-xs mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nom</Label>
                  <Input
                    placeholder="Ex: Martin"
                    value={memberForm.last_name}
                    onChange={(e) => setMemberForm({ ...memberForm, last_name: e.target.value })}
                    className="h-9 text-xs mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Adresse Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    required
                    placeholder="collaborateur@garage.fr"
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                    className="pl-9 h-9 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Mot de passe temporaire *</Label>
                <div className="relative mt-1">
                  <Key className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type="password"
                    required
                    placeholder="Min. 6 caractères"
                    value={memberForm.password}
                    onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                    className="pl-9 h-9 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Niveau de privilèges</Label>
                <select
                  value={memberForm.role}
                  onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                  className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <option value="member">👤 Collaborateur / Secrétaire (CA et montants masqués)</option>
                  <option value="owner">👑 Gérant associé (Accès total au CA et chiffrage)</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAddMemberModal(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  size="sm" 
                  disabled={submittingMember} 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {submittingMember ? "Création en cours..." : "Créer l'accès"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}